import {
  collection,
  doc,
  setDoc,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';

const COLLECTION = 'fundInvestorCapitalTranches';

/**
 * @typedef {'initial' | 'capital_addition' | 'repayment' | 'manual'} FundInvestorCapitalTrancheType
 */

function trancheDocId(fundInvestorId, type, sourceProposalId, sourceRepaymentId) {
  if (type === 'initial') return `fi_${fundInvestorId}_initial`;
  if (sourceProposalId) return `fi_${fundInvestorId}_prop_${sourceProposalId}`;
  if (sourceRepaymentId) return `fi_${fundInvestorId}_rep_${sourceRepaymentId}`;
  throw new Error('Tranche requires type initial, sourceProposalId, or sourceRepaymentId');
}

/**
 * Upsert a capital movement tranche (positive = capital in, negative = repayment).
 * Deterministic document ids — idempotent with Cloud Function backfill.
 *
 * @param {{
 *   fundInvestorId: string,
 *   lenderUid: string,
 *   amount: number,
 *   startDate: string,
 *   type: FundInvestorCapitalTrancheType,
 *   sourceProposalId?: string | null,
 *   sourceRepaymentId?: string | null,
 *   note?: string | null,
 * }} data
 * @returns {Promise<string>} tranche document id
 */
export async function upsertFundInvestorCapitalTranche(data) {
  const {
    fundInvestorId,
    lenderUid,
    amount,
    startDate,
    type,
    sourceProposalId,
    sourceRepaymentId,
    note,
  } = data;
  if (!fundInvestorId || !lenderUid || amount == null || Number.isNaN(Number(amount)) || Number(amount) === 0) {
    throw new Error('fundInvestorId, lenderUid and non-zero amount required');
  }
  const sd = startDate && String(startDate).trim().slice(0, 10);
  if (!sd) throw new Error('startDate (YYYY-MM-DD) required');

  const docId = trancheDocId(fundInvestorId, type, sourceProposalId, sourceRepaymentId);
  const ref = doc(firestore, COLLECTION, docId);
  await setDoc(
    ref,
    {
      fundInvestorId,
      lenderUid,
      amount: Number(amount),
      startDate: sd,
      type: type || 'initial',
      sourceProposalId: sourceProposalId || null,
      sourceRepaymentId: sourceRepaymentId || null,
      note: note && String(note).trim() ? String(note).trim().slice(0, 500) : null,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return docId;
}

/** @deprecated use upsertFundInvestorCapitalTranche */
export async function createFundInvestorCapitalTranche(data) {
  return upsertFundInvestorCapitalTranche(data);
}

/**
 * Manual / legacy tranche with unique id (multiple entries per day allowed).
 * @param {{ fundInvestorId: string, lenderUid: string, amount: number, startDate: string, note?: string | null }} data
 * @returns {Promise<string>} new tranche document id
 */
export async function addManualFundInvestorCapitalTranche(data) {
  const { fundInvestorId, lenderUid, amount, startDate, note } = data;
  if (!fundInvestorId || !lenderUid || amount == null || Number.isNaN(Number(amount)) || Number(amount) === 0) {
    throw new Error('fundInvestorId, lenderUid and non-zero amount required');
  }
  const sd = startDate && String(startDate).trim().slice(0, 10);
  if (!sd) throw new Error('startDate (YYYY-MM-DD) required');
  const docRef = await addDoc(collection(firestore, COLLECTION), {
    fundInvestorId,
    lenderUid,
    amount: Number(amount),
    startDate: sd,
    type: 'manual',
    sourceProposalId: null,
    sourceRepaymentId: null,
    note: note && String(note).trim() ? String(note).trim().slice(0, 500) : null,
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Ensure the invited principal exists as an `initial` tranche (idempotent).
 */
export async function ensureInitialFundInvestorTranche({ fundInvestorId, lenderUid, investedAmount, interestStartDate }) {
  const amount = Number(investedAmount) || 0;
  if (!fundInvestorId || !lenderUid || amount <= 0) return null;
  const start =
    interestStartDate && String(interestStartDate).trim().slice(0, 10)
      ? String(interestStartDate).trim().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  return upsertFundInvestorCapitalTranche({
    fundInvestorId,
    lenderUid,
    amount,
    startDate: start,
    type: 'initial',
    sourceProposalId: null,
    sourceRepaymentId: null,
    note: null,
  });
}

/**
 * @param {string} fundInvestorId
 * @param {string} lenderUid - required for Firestore rules (lender query)
 * @returns {Promise<Array<object>>}
 */
export async function getFundInvestorCapitalTranches(fundInvestorId, lenderUid) {
  if (!fundInvestorId || !lenderUid) return [];
  const q = query(
    collection(firestore, COLLECTION),
    where('fundInvestorId', '==', fundInvestorId),
    where('lenderUid', '==', lenderUid),
    orderBy('startDate', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Subscribe to tranches for a fund investor (lender).
 * @param {string} fundInvestorId
 * @param {string} lenderUid
 * @param {(rows: Array<object>) => void} callback
 * @returns {() => void}
 */
export function subscribeFundInvestorCapitalTranches(fundInvestorId, lenderUid, callback) {
  if (!fundInvestorId || !lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('fundInvestorId', '==', fundInvestorId),
    where('lenderUid', '==', lenderUid),
    orderBy('startDate', 'asc')
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('[fundInvestorCapitalTranches] snapshot error:', err?.code, err?.message);
      callback([]);
    }
  );
}
